import React, { useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { GAME_MODES, GAME_SKINS, LANDING_FEATURES, QUICK_STEPS } from '../data/siteContent';
import { useSiteContext } from '../context/SiteContext';

const previewSnake = new Set([18, 19, 20, 28, 36, 44]);
const previewHead = 44;
const previewFood = 53;

const HomePage: React.FC = () => {
  const location = useLocation();
  const { isSignedIn, profile, scores, topScore } = useSiteContext();
  const inviteName = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get('invite');
  }, [location.search]);

  const latestRuns = scores.slice(0, 3);

  return (
    <div className="page page--home">
      {inviteName ? (
        <section className="invite-banner">
          <p>
            <span className="site-chip">Invite detected</span>
          </p>
          <h2>{inviteName} just threw down a challenge.</h2>
          <p>
            Pick your mode, hit the grid, and put your own alias on the board.
          </p>
        </section>
      ) : null}

      <section className="hero">
        <div className="hero__copy">
          <p className="eyebrow">Neon arcade, classic instincts</p>
          <h1>Rule the grid. Chase the crown.</h1>
          <p className="hero__lede">
            Dive into fast Snake runs with bold skins, competitive modes, challenge links, and
            leaderboard bragging rights built for both desktop and mobile play.
          </p>

          <div className="hero__actions">
            <Link to="/play" className="button button--primary">
              Start a run
            </Link>
            <Link to="/leaderboard" className="button button--secondary">
              See the leaderboard
            </Link>
          </div>

          <div className="hero__stats">
            <article className="stat-card">
              <span>Arcade runs</span>
              <strong>{scores.length}</strong>
            </article>
            <article className="stat-card">
              <span>Top score</span>
              <strong>{topScore}</strong>
            </article>
            <article className="stat-card">
              <span>Current alias</span>
              <strong>{isSignedIn && profile ? profile.nickname : 'Guest mode'}</strong>
            </article>
          </div>
        </div>

        <aside className="hero__panel">
          <div className="hero-preview">
            <div className="hero-preview__header">
              <span className="site-chip">Live preview</span>
              <span className="hero-preview__label">Neon Grid / Infinity Loop</span>
            </div>

            <div className="hero-preview__board" aria-hidden="true">
              {Array.from({ length: 64 }, (_, index) => {
                const classNames = [
                  'hero-preview__cell',
                  previewSnake.has(index) ? 'hero-preview__cell--snake' : '',
                  index === previewHead ? 'hero-preview__cell--head' : '',
                  index === previewFood ? 'hero-preview__cell--food' : ''
                ]
                  .filter(Boolean)
                  .join(' ');

                return <span key={index} className={classNames} />;
              })}
            </div>

            <div className="hero-preview__footer">
              <div>
                <p>Available modes</p>
                <strong>{GAME_MODES.length}</strong>
              </div>
              <div>
                <p>Visual skins</p>
                <strong>{GAME_SKINS.length}</strong>
              </div>
              <div>
                <p>Latest top run</p>
                <strong>{latestRuns[0]?.score ?? 0}</strong>
              </div>
            </div>
          </div>
        </aside>
      </section>

      <section className="section-grid">
        {LANDING_FEATURES.map((feature) => (
          <article key={feature.title} className="feature-card">
            <span className="feature-card__accent">{feature.accent}</span>
            <h2>{feature.title}</h2>
            <p>{feature.description}</p>
          </article>
        ))}
      </section>

      <section className="content-section">
        <div className="section-heading">
          <p className="eyebrow">Choose your arena</p>
          <h2>Three ways to dominate the board.</h2>
          <p>
            From classic wall-crash survival to wraparound chaos and turbo-speed pressure, every
            mode changes the rhythm of the hunt.
          </p>
        </div>

        <div className="mode-grid">
          {GAME_MODES.map((mode) => (
            <article key={mode.id} className="mode-card">
              <span className="site-chip">{mode.badge}</span>
              <h3>{mode.name}</h3>
              <p>{mode.description}</p>
              <div className="mode-card__meta">
                <span>{mode.speed}ms tick</span>
                <span>{mode.wallBehavior === 'wrap' ? 'Wrap walls' : 'Solid walls'}</span>
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="content-section content-section--split">
        <div>
          <div className="section-heading">
            <p className="eyebrow">Built to compete</p>
            <h2>Fast runs, sharp style, and score-chasing momentum.</h2>
            <p>
              Pick a loadout, jump into the action, and keep climbing as your hottest runs and
              favorite setups shape the full arcade experience.
            </p>
          </div>

          <div className="steps-grid">
            {QUICK_STEPS.map((step, index) => (
              <article key={step.title} className="step-card">
                <span className="step-card__index">0{index + 1}</span>
                <h3>{step.title}</h3>
                <p>{step.description}</p>
              </article>
            ))}
          </div>
        </div>

        <aside className="activity-panel">
          <div className="section-heading section-heading--compact">
            <p className="eyebrow">Hot runs</p>
            <h2>Recent action on the board</h2>
          </div>

          {latestRuns.length > 0 ? (
            <div className="activity-list">
              {latestRuns.map((entry) => (
                <article key={entry.id} className="activity-item">
                  <div>
                    <strong>{entry.nickname}</strong>
                    <p>{entry.modeId}</p>
                  </div>
                  <div className="activity-item__score">{entry.score}</div>
                </article>
              ))}
            </div>
          ) : (
            <p className="empty-state">
              The board is waiting. Launch a run and set the pace.
            </p>
          )}
        </aside>
      </section>
    </div>
  );
};

export default HomePage;
